import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import TicketPanel from './TicketPanel.js';
import { 
  observeAuthState,
  checkUserAccess,
  getMaintenanceMessage,
  subscribeToMaintenanceStatus,
  getUserProfile,
  generateDeviceUniqueId
} from './ticketSystemApi.js';

function App() {
  const [user, setUser] = useState(null);
  const [canonicalUserId, setCanonicalUserId] = useState(null);
  const [maintenanceStatus, setMaintenanceStatus] = useState({ allowed: true, reason: 'normal' });
  const [loading, setLoading] = useState(true);
  const [maintenanceLang, setMaintenanceLang] = useState('en'); // デフォルトは英語

  // ウィンドウタイトルを設定
  useEffect(() => {
    document.title = 'Ticket System';
  }, []);

  useEffect(() => {
    // 認証状態を監視
    const unsubscribeAuth = observeAuthState(async (authUser) => {
      console.log('[App] 認証状態変更:', authUser ? `UID: ${authUser.uid}` : 'ログアウト');
      setUser(authUser);
      
      if (authUser) {
        // ユーザープロフィールからcanonicalUserIdを取得
        try {
          const profile = await getUserProfile(authUser.uid);
          if (profile) {
            setCanonicalUserId(authUser.uid); // プロフィールのドキュメントID = canonicalUserId
            console.log('[App] Canonical User ID:', authUser.uid);
          } else {
            setCanonicalUserId(authUser.uid);
          }
        } catch (error) {
          console.warn('[App] プロフィール取得失敗:', error);
          setCanonicalUserId(authUser.uid);
        }
      } else {
        setCanonicalUserId(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!user || !canonicalUserId) return;

    const deviceId = generateDeviceUniqueId();
    console.log('[App] メンテナンスチェック開始');
    console.log('[App] - Firebase UID:', user.uid);
    console.log('[App] - Email:', user.email);
    console.log('[App] - Canonical User ID:', canonicalUserId);
    console.log('[App] - Device ID:', deviceId);

    // メンテナンスステータスをリアルタイム監視
    const unsubscribeMaintenance = subscribeToMaintenanceStatus(async (status, excludeUsers) => {
      try {
        console.log('[App] メンテナンスステータス更新:', status, 'excludeUsers:', excludeUsers);
        const accessResult = await checkUserAccess(user.uid, user.email, canonicalUserId, deviceId);
        console.log('[App] アクセス結果:', accessResult);
        setMaintenanceStatus(accessResult);
      } catch (error) {
        console.error('[App] メンテナンスチェックエラー:', error);
        // エラー時は通常モードとして扱う
        setMaintenanceStatus({ allowed: true, reason: 'normal' });
      }
    });

    // 初回チェック
    checkUserAccess(user.uid, user.email, canonicalUserId, deviceId)
      .then(result => {
        console.log('[App] 初回メンテナンスチェック結果:', result);
        setMaintenanceStatus(result);
      })
      .catch(error => {
        console.error('[App] 初回メンテナンスチェックエラー:', error);
        // エラー時は通常モードとして扱う
        setMaintenanceStatus({ allowed: true, reason: 'normal' });
      });

    return () => unsubscribeMaintenance();
  }, [user, canonicalUserId]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <p>Loading...</p>
      </div>
    );
  }

  // user が null のときも TicketPanel をそのまま表示（TicketPanel 内でログイン画面を表示する）
  // ※ここで条件分岐すると認証変化のたびにアンマウント→再マウントが発生し状態がリセットされる

  if (user && !maintenanceStatus.allowed) {
    const messages = maintenanceStatus.messages || {};
    console.log('[App] メンテナンスメッセージ:', messages);
    console.log('[App] 選択言語:', maintenanceLang);
    console.log('[App] 表示メッセージ:', messages[maintenanceLang]);
    
    const message = messages[maintenanceLang] || messages.en || 'System is under maintenance. Please wait.';
    
    const langLabels = {
      en: 'English',
      ja: '日本語',
      zh: '中文',
      ko: '한국어'
    };
    
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        padding: '20px',
        textAlign: 'center',
        gap: '20px'
      }}>
        <h2>🔧 Maintenance / メンテナンス中</h2>
        <p style={{ fontSize: '16px', maxWidth: '600px' }}>{message}</p>
        
        <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
          {['en', 'ja', 'zh', 'ko'].map(lang => (
            <button
              key={lang}
              onClick={() => setMaintenanceLang(lang)}
              style={{
                padding: '8px 16px',
                border: maintenanceLang === lang ? '2px solid #3b82f6' : '1px solid #d1d5db',
                borderRadius: '6px',
                background: maintenanceLang === lang ? '#eff6ff' : 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: maintenanceLang === lang ? '600' : '400'
              }}
            >
              {langLabels[lang]}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return <TicketPanel />;
}

const container = document.getElementById('root');
createRoot(container).render(React.createElement(App));
